import type { ReactElement, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { MoreVertical } from 'lucide-react';

export interface SplitButtonProps {
  className?: string;
  /** Classes applied to both inner Button components (useful for font-weight, etc.) */
  buttonClassName?: string;
  variant?:
    | 'default'
    | 'destructive'
    | 'outline'
    | 'outline-solid'
    | 'secondary'
    | 'ghost'
    | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  primaryAction: {
    children: ReactNode;
    onClick: () => void;
    disabled?: boolean;
  };
  menuTrigger: {
    // Rendered as the sole child of an `asChild` Button (→ Slot) below, which
    // requires a single valid element — `ReactNode` would let a caller pass
    // text and throw at render under @radix-ui/react-slot 1.3.3 (VALE-1280).
    children?: ReactElement;
    disabled?: boolean;
  };
}

export const SplitButton = ({
  ref,
  className,
  buttonClassName,
  variant = 'outline-solid',
  size = 'sm',
  primaryAction,
  menuTrigger
}: SplitButtonProps & {
  ref?: React.RefObject<HTMLDivElement | null>;
}) => {
  return (
    <div
      ref={ref}
      className={cn('inline-flex items-center rounded-md', className)}
    >
      <Button
        variant={variant}
        size={size}
        onClick={primaryAction.onClick}
        disabled={primaryAction.disabled}
        className={cn(
          'rounded-r-none border-r-0 focus:z-10',
          size === 'sm' && 'h-7',
          buttonClassName
        )}
      >
        {primaryAction.children}
      </Button>
      <Button
        variant={variant}
        size={size}
        disabled={menuTrigger.disabled}
        className={cn(
          'rounded-l-none px-1 focus:z-10',
          size === 'sm' && 'h-7 w-7',
          buttonClassName
        )}
        asChild
      >
        {menuTrigger.children || (
          <span>
            <MoreVertical className="h-3 w-3" />
          </span>
        )}
      </Button>
    </div>
  );
};

SplitButton.displayName = 'SplitButton';
